import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Mail } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AdminForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || busy) return;
    setBusy(true);
    try {
      // Always 200 whether or not the email is a real admin — same success
      // screen either way, no admin-account enumeration.
      await axios.post(`${BASE}/admin/forgot-password`, { email: email.trim() });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 grid place-items-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="text-3xl font-black text-white tracking-tight mb-1">Brainydo</div>
          <div className="text-slate-400 text-sm font-medium tracking-wide uppercase">Super Admin</div>
        </div>
        <div className="bg-white rounded-2xl p-8 shadow-2xl">
          {sent ? (
            <>
              <div className="w-11 h-11 rounded-full bg-emerald-50 grid place-items-center mb-4">
                <CheckCircle2 size={20} className="text-emerald-600" />
              </div>
              <h1 className="text-[17px] font-bold text-slate-900 mb-2">Check your email</h1>
              <p className="text-[13px] text-slate-600 leading-relaxed">
                If <strong>{email.trim()}</strong> is an admin account, we've sent a reset link.
                It expires in 60 minutes.
              </p>
              <Link
                to="/admin/login"
                className="mt-6 inline-flex items-center gap-1.5 text-[13px] text-slate-600 hover:text-slate-900"
              >
                <ArrowLeft size={14} /> Back to sign in
              </Link>
            </>
          ) : (
            <>
              <h1 className="text-[17px] font-bold text-slate-900 mb-1.5">Forgot your password?</h1>
              <p className="text-[13px] text-slate-500 mb-6">
                Enter your admin email and we'll send you a reset link.
              </p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    placeholder="admin@example.com"
                    className="w-full h-10 px-3 border border-slate-200 rounded-xl text-[13.5px] focus:outline-none focus:ring-2 focus:ring-slate-900"
                  />
                </div>
                <button
                  type="submit"
                  disabled={busy || !email.trim()}
                  className="w-full h-10 inline-flex items-center justify-center gap-2 bg-slate-900 text-white rounded-xl text-[13.5px] font-semibold hover:bg-slate-700 disabled:opacity-50 transition active:scale-95"
                >
                  <Mail size={14} />
                  {busy ? "Sending…" : "Send reset link"}
                </button>
                <p className="text-center pt-1">
                  <Link
                    to="/admin/login"
                    className="text-[12.5px] text-slate-500 hover:text-slate-900 underline underline-offset-2"
                  >
                    Back to sign in
                  </Link>
                </p>
              </form>
            </>
          )}
        </div>
        <p className="text-center text-slate-600 text-xs mt-6">Restricted access — authorised personnel only</p>
      </div>
    </div>
  );
}
